import { Suspense, lazy, useEffect, useMemo, useState } from 'react'
import { detectWebGL, usePrefersReducedMotion, useIsMobile } from './lib/env'
import Fallback from './components/Fallback'
import Cursor from './components/Cursor'
import './components/ui.css'

// Lazy-load the heavy WebGL bundle so fallback users never download three.js.
const Experience = lazy(() => import('./Experience'))
// The product suite is its own (no-WebGL-required) destination, also code-split.
const SuiteApp = lazy(() => import('./suite/SuiteApp'))

function useHashRoute() {
  const [hash, setHash] = useState(() => (typeof window !== 'undefined' ? window.location.hash : ''))
  useEffect(() => {
    const onHash = () => setHash(window.location.hash)
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])
  return hash
}

function Loader() {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        background: 'var(--graphite-900)',
      }}
    >
      <span
        style={{
          fontFamily: 'var(--serif)',
          letterSpacing: '0.4em',
          color: 'var(--gold-300)',
          fontSize: '1rem',
          opacity: 0.8,
          paddingLeft: '0.4em',
          animation: 'sov-pulse 1.8s ease-in-out infinite',
        }}
      >
        SOVEREIGN
      </span>
      <style>{`@keyframes sov-pulse{0%,100%{opacity:.35}50%{opacity:.95}}`}</style>
    </div>
  )
}

export default function App() {
  const webgl = useMemo(() => detectWebGL(), [])
  const reduced = usePrefersReducedMotion()
  const mobile = useIsMobile()
  // Dev/QA hook: ?view=flat previews the crafted static fallback on a WebGL device.
  const forceFlat = useMemo(
    () =>
      import.meta.env.DEV &&
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('view') === 'flat',
    [],
  )

  const route = useHashRoute()

  // The product suite is a standalone destination (works without WebGL).
  if (route.startsWith('#/suite')) {
    return (
      <>
        <Cursor />
        <Suspense fallback={<Loader />}>
          <SuiteApp />
        </Suspense>
      </>
    )
  }

  // Honesty + craft: no-WebGL and reduced-motion users get the crafted static view.
  const content = !webgl ? (
    <Fallback reason="no-webgl" />
  ) : reduced || forceFlat ? (
    <Fallback reason="reduced-motion" />
  ) : (
    <Suspense fallback={<Loader />}>
      <Experience mobile={mobile} reduced={false} />
    </Suspense>
  )

  return (
    <>
      <Cursor />
      {content}
    </>
  )
}
